// src/pages/SupportPage.jsx
//
// The support page. Two jobs:
//
//   1. Answer the questions people actually write in about — what's free, what
//      the one payment buys, how to get a purchase back, and how refunds work.
//   2. Be the Support URL in App Store Connect. Apple requires one, reviewers
//      open it, and "could not find support information" is a rejection. Until
//      this existed, /support fell through the SPA catch-all to the home page:
//      it returned 200 and showed a reviewer something that was not support.
//
// Modelled on Pemberton's support page (pocket-butler-marketing/src/pages/
// support.astro), which is the fleet reference.
//
// Australian English throughout, and market-aware: prices are named in the
// storefront's own terms rather than assuming an Australian reader.

import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  LifeBuoy, ChevronLeft, CreditCard, RefreshCw, UserCircle,
  MessageCircle, Mail, Trash2,
} from 'lucide-react';
import Card from '../components/shared/Card';

// Inside the iOS app nothing on this page may point towards buying outside the
// App Store (rule 3.1.1): existing members are told to sign in, nothing more.
const IS_NATIVE = Capacitor.isNativePlatform();

const Section = ({ icon: Icon, title, children }) => (
  <Card className="p-6 sm:p-8 mb-6">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-bright-gold/15 flex items-center justify-center shrink-0" aria-hidden="true">
        <Icon className="w-5 h-5 text-bright-gold" />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-white">{title}</h2>
    </div>
    <div className="space-y-5">{children}</div>
  </Card>
);

const QA = ({ q, children }) => (
  <div>
    <h3 className="text-base sm:text-lg font-semibold text-white mb-2">{q}</h3>
    <div className="text-sm sm:text-base text-pearl/75 space-y-3">{children}</div>
  </div>
);

const SupportPage = () => (
  <>
    <title>Support | Profound Philosophy</title>
    <meta name="description" content="Help with Profound Philosophy: what's free, what the full course includes, restoring a purchase, refunds, your account, and how to reach us." />
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" className="flex items-center gap-2 text-amber-300 hover:text-white mb-8 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl mb-6">
              <LifeBuoy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">Support</h1>
            <p className="text-pearl/75 max-w-2xl mx-auto">
              Everything we get asked, answered. If your question isn't here, write to us,
              and a real person reads it.
            </p>
          </div>

          <Section icon={CreditCard} title="What's free, and what you pay for">
            <QA q="What can I use without paying anything?">
              <p>
                A good deal of it, and permanently: the Philosophical Profile assessment,
                the first two modules (<em>What Is Philosophy?</em> and{' '}
                <em>Logic &amp; Reasoning</em>) and the four tools that go with them, the
                Lineage of thinkers, your Journal, the Resources, your Dashboard, and the
                Original Position played on your own.
              </p>
              <p>
                You can also join a linked table someone else is hosting, free, whether or
                not you've bought anything.
              </p>
            </QA>
            <QA q="What does the full course include?">
              <p>
                All eight modules, all eighteen tools in the Toolkit, and the Original
                Position's table modes: Parlour, played by passing one phone around a
                table, and hosting a linked table across everyone's own phones.
              </p>
              <p>
                It is a <strong>single payment, not a subscription</strong>. Once it's
                yours, it stays yours.
              </p>
            </QA>
            <QA q="What does it cost?">
              <p>
                The price you see in the app is your own store's price in your own
                currency. Apple sets each country's equivalent, so it differs between the
                markets this app serves. The app always shows the real figure before you
                confirm anything.
              </p>
            </QA>
          </Section>

          <Section icon={RefreshCw} title="Restoring a purchase">
            <QA q="I've reinstalled the app, or changed phones. Where has it gone?">
              <p>
                Your purchase belongs to your Apple Account and follows you. Open the unlock
                screen and choose <strong>Restore a previous purchase</strong>, making sure
                you're signed in to the same Apple Account you bought it with.
              </p>
              <p>
                Better still, sign in to your free Dive Deep Dialogues account in the app
                before you buy. Then the purchase is tied to that account as well, and it
                unlocks the web version on any device you sign in to.
              </p>
            </QA>
            <QA q="I bought it on my phone, but the website still wants payment.">
              <p>
                That happens when the purchase was made while signed out, so there was no
                account to attach it to. Sign in on the phone, open the unlock screen and
                choose <strong>Restore a previous purchase</strong>. That links it to your
                account, and the web will catch up the next time you sign in. If it doesn't,
                write to us and we'll put it right.
              </p>
            </QA>
            <QA q="How do refunds work?">
              <p>
                In-app purchases are handled by Apple, not by us, so refund requests go to
                them directly: visit{' '}
                <a href="https://reportaproblem.apple.com" className="text-amber-300 hover:underline" target="_blank" rel="noopener noreferrer">
                  reportaproblem.apple.com
                </a>{' '}
                <span className="sr-only">(opens in a new tab)</span>, sign in, and select
                the purchase.{!IS_NATIVE && ' For a Dive Deep Dialogues membership bought on the web, email us instead.'}
              </p>
            </QA>
          </Section>

          <Section icon={MessageCircle} title="Sofia, your philosophical companion">
            <QA q="Is Sofia included?">
              <p>
                Sofia is available to everyone with a free Dive Deep Dialogues account, with
                a daily allowance of messages.{IS_NATIVE
                  ? ' If you’re already a Dive Deep Dialogues member, sign in with the same account and your membership’s allowance applies.'
                  : ' A Dive Deep Dialogues membership raises that allowance and shares it across every app in the collection.'}
              </p>
              <p>
                She isn't part of the one-off purchase, which buys the course itself.
                {!IS_NATIVE && ' If you have bought the full course and want more of Sofia as well, a membership is the thing to look at.'}
              </p>
            </QA>
            <QA q="Sofia says I've reached today's limit.">
              <p>
                The allowance resets once a day on the UTC clock, so the hour it comes back depends
                on where you are. Nothing you've written is lost, and your conversation will be
                waiting.
              </p>
            </QA>
          </Section>

          <Section icon={UserCircle} title="Your account">
            <QA q="Do I need an account?">
              <p>
                Not to read, or to use the free tools. The app works signed out and keeps
                your progress on the device you're using. An account saves your work to the
                cloud, carries it between devices, and lets you talk to Sofia.
              </p>
            </QA>
            <QA q="I can't sign in.">
              <p>
                Sign-in runs through the Dive Deep Dialogues account hub, shared by every app
                in the collection, so the same details work everywhere. If a reset email
                doesn't arrive, check your spam folder first, then write to us.
              </p>
            </QA>
          </Section>

          <Section icon={Trash2} title="Your data">
            <QA q="How do I delete everything?">
              <p>
                The <Link to="/privacy" className="text-amber-300 hover:underline">Data &amp; Privacy page</Link>{' '}
                lets you clear what this app has kept on your device, and change your Google
                Analytics choice. To delete your Dive Deep Dialogues account and everything held
                against it across every app, use the account hub. Deletion there reaches all
                of it, which is precisely why your data lives in one place rather than
                scattered across separate apps.
              </p>
            </QA>
          </Section>

          <Section icon={Mail} title="Still stuck?">
            <QA q="Talk to a person">
              <p>
                Email{' '}
                <a href="mailto:help@divedeepdialogues.com" className="text-amber-300 hover:underline">
                  help@divedeepdialogues.com
                </a>
                . Tell us what happened and what you expected instead, and if it's about a
                purchase, the email address on your Dive Deep Dialogues account. We answer
                every one.
              </p>
            </QA>
          </Section>

          <p className="text-center text-sm text-pearl/50 mt-10">
            <Link to="/privacy" className="text-amber-300 hover:underline">Data &amp; Privacy</Link>
            {' · '}
            <Link to="/terms" className="text-amber-300 hover:underline">Terms of Use</Link>
          </p>
        </motion.div>
      </div>
    </div>
  </>
);

export default SupportPage;
